import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProducts } from '../../hooks/useProducts';
import { useCart } from '../../hooks/useCart';
import { Button, Card, Loading } from '../common';
import { formatCurrency } from '../../utils/format';
import {
  Star,
  Minus,
  Plus,
  ShoppingCart,
  Heart,
  Share2,
  Truck,
  ShieldCheck,
  RefreshCw,
  Leaf,
  Check,
} from 'lucide-react';

const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getProductById, isLoading } = useProducts();
  const { addToCart, isInCart } = useCart();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  const product = getProductById(id || '');
  const inCart = product ? isInCart(product.id) : false;

  if (isLoading) {
    return <Loading fullScreen text="Loading product details..." />;
  }

  if (!product) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-dark-dark dark:bg-white transition-colors duration-300">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-300 dark:text-gray-600 mb-4 transition-colors duration-300">
            Product Not Found
          </h2>
          <p className="text-gray-400 dark:text-gray-500 mb-6 transition-colors duration-300">
            The product you're looking for doesn't exist.
          </p>
          <Button variant="primary" onClick={() => navigate('/products')}>
            Browse Products
          </Button>
        </div>
      </div>
    );
  }

  const handleQuantityChange = (delta: number) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= product.stockQuantity) {
      setQuantity(newQuantity);
    }
  };

  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="mb-8 text-sm">
        <ol className="flex items-center gap-2">
          <li>
            <button
              onClick={() => navigate('/')}
              className="text-gray-400 dark:text-gray-500 hover:text-accent dark:hover:text-primary transition-colors duration-300"
            >
              Home
            </button>
          </li>
          <li className="text-gray-500 dark:text-gray-400">/</li>
          <li>
            <button
              onClick={() => navigate('/products')}
              className="text-gray-400 dark:text-gray-500 hover:text-accent dark:hover:text-primary transition-colors duration-300"
            >
              Products
            </button>
          </li>
          <li className="text-gray-500 dark:text-gray-400">/</li>
          <li className="text-accent dark:text-primary font-medium capitalize transition-colors duration-300">
            {product.category}
          </li>
        </ol>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Image Gallery */}
        <div className="space-y-4">
          {/* Main Image */}
          <div className="aspect-square bg-dark-light dark:bg-gray-100 rounded-2xl overflow-hidden relative transition-colors duration-300">
            <img
              src={product.images[selectedImage]}
              alt={product.name}
              className="w-full h-full object-cover"
            />
            {discount > 0 && (
              <span className="absolute top-4 left-4 bg-red-500 text-white font-bold px-3 py-1 rounded-full">
                -{discount}% OFF
              </span>
            )}
            {product.organic && (
              <span className="absolute top-4 right-4 bg-accent dark:bg-primary text-white font-bold px-3 py-1 rounded-full flex items-center gap-1 transition-colors duration-300">
                <Leaf className="w-4 h-4" />
                Organic
              </span>
            )}
          </div>

          {/* Thumbnail Images */}
          {product.images.length > 1 && (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {product.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    selectedImage === index
                      ? 'border-accent dark:border-primary'
                      : 'border-transparent hover:border-gray-300 dark:hover:border-gray-400'
                  } transition-colors duration-300`}
                >
                  <img src={image} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="space-y-6">
          {/* Category & Title */}
          <div>
            <p className="text-accent dark:text-primary font-medium uppercase tracking-wide mb-2 transition-colors duration-300">
              {product.category}
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
              {product.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${
                      i < Math.floor(product.rating)
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300 dark:text-gray-400'
                    }`}
                  />
                ))}
                <span className="ml-2 font-medium text-white dark:text-dark-dark transition-colors duration-300">
                  {product.rating}
                </span>
              </div>
              <span className="text-gray-400 dark:text-gray-500">|</span>
              <span className="text-gray-400 dark:text-gray-500 transition-colors duration-300">
                {product.reviews} reviews
              </span>
            </div>
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-3">
            <span className="text-4xl font-bold text-accent dark:text-primary transition-colors duration-300">
              {formatCurrency(product.price)}
            </span>
            {product.originalPrice && (
              <span className="text-xl text-gray-400 dark:text-gray-500 line-through transition-colors duration-300">
                {formatCurrency(product.originalPrice)}
              </span>
            )}
            <span className="text-gray-400 dark:text-gray-500 transition-colors duration-300">
              / {product.unit}
            </span>
          </div>

          {/* Description */}
          <p className="text-gray-300 dark:text-gray-600 leading-relaxed transition-colors duration-300">
            {product.description}
          </p>

          {/* Stock Status */}
          <div className="flex items-center gap-2">
            {product.inStock ? (
              <>
                <Check className="w-5 h-5 text-accent dark:text-primary transition-colors duration-300" />
                <span className="text-accent dark:text-primary font-medium transition-colors duration-300">
                  In Stock ({product.stockQuantity} available)
                </span>
              </>
            ) : (
              <span className="text-red-500 font-medium">Out of Stock</span>
            )}
          </div>

          {/* Quantity Selector */}
          {product.inStock && (
            <div className="flex items-center gap-4">
              <span className="font-medium text-gray-300 dark:text-gray-600 transition-colors duration-300">
                Quantity:
              </span>
              <div className="flex items-center border border-dark-light dark:border-gray-200 rounded-lg transition-colors duration-300">
                <button
                  onClick={() => handleQuantityChange(-1)}
                  className="p-3 text-gray-400 dark:text-gray-500 hover:text-accent dark:hover:text-primary transition-colors duration-300"
                  disabled={quantity <= 1}
                >
                  <Minus className="w-5 h-5" />
                </button>
                <span className="w-16 text-center font-medium text-lg text-white dark:text-dark-dark transition-colors duration-300">
                  {quantity}
                </span>
                <button
                  onClick={() => handleQuantityChange(1)}
                  className="p-3 text-gray-400 dark:text-gray-500 hover:text-accent dark:hover:text-primary transition-colors duration-300"
                  disabled={quantity >= product.stockQuantity}
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <Button
              variant="primary"
              size="lg"
              className="flex-1"
              leftIcon={<ShoppingCart className="w-5 h-5" />}
              onClick={() => addToCart(product, quantity)}
              disabled={!product.inStock || inCart}
            >
              {inCart ? 'Added to Cart' : product.inStock ? 'Add to Cart' : 'Out of Stock'}
            </Button>
            <Button variant="outline" size="lg" className="px-4">
              <Heart className="w-5 h-5" />
            </Button>
            <Button variant="outline" size="lg" className="px-4">
              <Share2 className="w-5 h-5" />
            </Button>
          </div>

          {/* Features */}
          <Card className="p-4 space-y-3" padding="none">
            <div className="flex items-center gap-3 text-gray-300 dark:text-gray-600 transition-colors duration-300">
              <Truck className="w-5 h-5 text-accent dark:text-primary transition-colors duration-300" />
              <span>Free delivery on orders over $50</span>
            </div>
            <div className="flex items-center gap-3 text-gray-300 dark:text-gray-600 transition-colors duration-300">
              <ShieldCheck className="w-5 h-5 text-accent dark:text-primary transition-colors duration-300" />
              <span>Fresh quality guarantee</span>
            </div>
            <div className="flex items-center gap-3 text-gray-300 dark:text-gray-600 transition-colors duration-300">
              <RefreshCw className="w-5 h-5 text-accent dark:text-primary transition-colors duration-300" />
              <span>Easy returns within 7 days</span>
            </div>
          </Card>

          {/* Origin */}
          <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 transition-colors duration-300">
            <span>Origin:</span>
            <span className="font-medium">{product.origin}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;